import { Controller } from 'stimulus'
import { useDebounce } from 'stimulus-use'
import isEqual from 'lodash.isequal'

/**
 * @namespace CollectionSelect
 */
export default class extends Controller {
  static get targets() {
    return ['query', 'queryField', 'results']
  }

  static values = {
    apiEndpointPath: String,
    filterComponent: String,
    queryMinChars: Number,
    extraQueryParams: Object
  }

  /**
     * Set up certain keyboard methods with a debounce, so they don't fire on every user keystroke.
     * {@link https://github.com/stimulus-use/stimulus-use/blob/master/docs/use-debounce.md}
     */
  static get debounces() {
    return ['initiateSearch']
  }

  /**
     * Set the minimum trigger value length, for use when the trigger is an input element.
     */
  connect() {
    useDebounce(this, { wait: 1000 })
    this.element[this.identifier] = this
    this.apiEndpointPath = this.apiEndpointPathValue || this.element.dataset.apiEndpointPath
    this.queryMinimumValueLength = this.queryMinCharsValue || 1
  }

  /**
     * Get the whole endpoint URL
     * @param {Array} queryKeyValuePairs
     */
  apiEndpointUrl(queryKeyValuePairs) {
    let url = `${this.apiEndpointPath}`

    if (url.includes('linked_items') || url.includes('search_v2')) {
      queryKeyValuePairs.push('filter_component=' + this.filterComponentValue)
    }

    if (url.includes('?')) {
      url += '&'
    } else {
      url += '?'
    }

    url += queryKeyValuePairs.join('&')
    url += '&' + $.param(this.extraQueryParamsValue)

    return url
  }

  mergeQueryStringParams(params) {
    this.extraQueryParamsValue = { ...this.extraQueryParamsValue, ...params }
  }

  /**
     * Go and fetch the search results from the endpoint
     * {@link https://lodash.com/docs/4.17.15#isEqual}
     */
  requestResults() {
    let requestedQueryKeyValuePairs = this.currentQueryKeyValueString()

    this.beforeRequestResults()
    fetch(this.apiEndpointUrl(requestedQueryKeyValuePairs))
      .then(response => response.text())
      .then((html) => {
        requestedQueryKeyValuePairs = requestedQueryKeyValuePairs.filter(pair => !pair.startsWith('filter_component'))

        if (isEqual(requestedQueryKeyValuePairs, this.currentQueryKeyValueString())) {
          this.renderResults(html)
        }

        this.afterRequestResults()
      })
  }

  beforeRequestResults() {
    this.queryFieldTargets.forEach((elem) => elem.setAttribute('loading', true))
  }

  afterRequestResults() {
    this.queryFieldTargets.forEach((elem) => elem.removeAttribute('loading'))
  }

  /**
     * Initiate a query search after a user types into the main input.
     * Guard against making an API request, if there aren't enough characters in the trigger query.
     * @param {KeyboardEvent} event
     */
  initiateSearch(event) {
    if (event.target.value.length >= this.queryMinimumValueLength) {
      this.requestResults()
    }
  }

  /**
     * Render the results
     * @param {HTMLElement[]} html
     * @see {Menu.handleMenuItemsLoaded}
     */
  renderResults(html) {
    $(this.resultsTarget).html(html)
    // TODO: Put the live region logic here directly?
    const resultsLoadedEvent = new CustomEvent('results-loaded')
    window.dispatchEvent(resultsLoadedEvent)
  }

  /**
   * Return the current query as key value pairs.
   */
  currentQueryKeyValuePairs() {
    const pairs = {}
    this.queryTargets.forEach((query) => {
      if (query.name.endsWith('[]')) {
        if (!pairs[query.name]) pairs[query.name] = []
        pairs[query.name].push(query.value)
      } else {
        pairs[query.name] = query.value
      }
    })
    return pairs
  }

  /**
     * Return the current query as key values string.
     */
  currentQueryKeyValueString() {
    return this.queryTargets.map(query => `${query.name}=${encodeURIComponent(query.value)}`)
  }
}
